import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService, User } from '../../../shared/services/users/users.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss']
})
export class UserDetailComponent implements OnInit, OnDestroy {
  
  private subscriptions: Subscription[] = [];
  // User data
  userId: number | null = null;
  user: User | null = null;
  isLoading = true;
  
  // Navigation
  origin: string = 'users'; // Default origin
  householdId: string | null = null;
  
  // Section navigation
  activeSection: 'general' | 'files' | 'access' | 'activity' | 'households' = 'general';
  sections: { id: string; label: string }[] = [];

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private usersService: UsersService,
    private toaster: ToasterService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      // Get user ID from route params
      this.route.paramMap.subscribe(params => {
        const id = params.get('id');
        if (id) {
          this.userId = +id;
          this.loadUserDetails();
      }
      }),
    
      // Get active section and origin from query params
      this.route.queryParamMap.subscribe(params => {
        // Get section
        const section = params.get('section');
        const validSections = ['general', 'files', 'access', 'activity', 'households'];
        if (section && validSections.includes(section)) {
          this.activeSection = section as 'general' | 'files' | 'access' | 'activity' | 'households';
        } else if (this.userId) {
          // Default to general section if no valid section is provided
          this.updateQueryParams('general');
        }
        
        // Get origin
        const origin = params.get('origin');
        if (origin) {
          this.origin = origin;
        }

        const householdId = params.get('householdId');
        if (householdId) {
          this.householdId = householdId;
        }
      })
    );
  }

  /**
   * Load user details from the API
   */
  private async loadUserDetails(): Promise<void> {
    if (!this.userId) return;
    
    this.isLoading = true;
    
    try {
      // Use listUsers with specific userId filter to get user details
      const response = await this.usersService.getById(this.userId);
      console.log(response);
      this.user = response;
      this.setupSections();
    } catch (error: any) {
      this.toaster.warning(`Error loading user details: ${error?.message ?? error}`, 4000);
      console.error('Error loading user details:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Navigate to a different section
   */
  private setupSections(): void {
    if (!this.user) {
      this.sections = [];
      return;
    }

    const defaultSections = [
      { id: 'general', label: 'General' },
      { id: 'files', label: 'Files' },
      { id: 'access', label: 'Access' },
      { id: 'activity', label: 'Activity' }
    ];

    this.sections = [...defaultSections];

    const role = this.user.ROLE.toUpperCase();
    if (role === 'MANAGER' || role === 'ADVISOR') {
      this.sections.push({ id: 'households', label: 'Households' });
    }
  }

  /**
   * Navigate to a different section
   */
  public navigateToSection(sectionId: string): void {
    if (this.sections.some(s => s.id === sectionId)) {
      this.activeSection = sectionId as 'general' | 'files' | 'access' | 'activity' | 'households';
      this.updateQueryParams(sectionId);
    }
  }
  
  /**
   * Navigate back to the previous view based on origin
   */
  public navigateBack(): void {
    switch (this.origin) {
      case 'households':
        this.router.navigate(['/households', this.householdId, 'users']);
        break;
      case 'users-list':
        this.router.navigate(['/users-list']);
        break;
      case 'admin':
        this.router.navigate(['/users/admin']);
        break;
      case 'users-households':
        this.router.navigate(['/users/households']);
        break;
      default:
        this.router.navigate(['/users/application']);
        break;
    }
  }

  /**
   * Update query params without reloading the page
   */
  private updateQueryParams(section: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { section },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
